import {
    ArrayHelper,
    CalendarModel,
    DateHelper,
    EventModel,
    Model,
    SchedulerPro,
    StateTrackingManager
} from '@bryntum/schedulerpro/schedulerpro.module.js';
import { addDays, format, isAfter, isValid } from 'date-fns';
import { cloneDeep, differenceBy, isEmpty, isEqual, keyBy, remove, uniqBy } from 'lodash';
import { createSchedulerProject } from '../common/components/schedule/utils/scheduler-instance-manager';
import { CONSTANTS_ID, FETCH_POLICY_CONFIG, FILTER_IDENTIFIERS, TIME_CONSTANTS } from '../common/constants/constants';
import { ENTITY_CONSTANTS } from '../common/constants/entityConstants';
import type { SubstitutionMapType } from '../common/i18n/I18n';
import db from '../common/indexedDB';
import { searchClient, slimResultsQuery } from '../common/list/slimQuery';
import { AppDefinition } from '../common/nav/apps';
import { log } from '../common/utils/commonUtils';
import {
    API_DATE_FORMAT,
    breakDateRangeIntoMonths,
    DateRange,
    destructureSchedule,
    isAtLeast24hoursApart,
    toDateTimeISOWithoutOffset
} from '../common/utils/dateTime';
import { FILTER_FIELD_NAMES } from '../production/helpers/productionUtils';
import { STATE_TYPE } from '../production/productionConstants';
import { SetIsSchedulerProductionRefetchNeeded } from '../production/schedule/ActivityForm';
import { roundUpByDurationUnit } from '../supply/booking/ScheduleCalculator';
import { UTILIZATION_UNITS_MAPPING } from '../supply/dashboard/BryntumAdapter';
import {
    MOVE_SCHEDULER_NAME,
    SCHEDULER_INDEX_DB_TABLE,
    SCHEDULER_STORE_KEY,
    TEMP_NAME,
    TIME_UNITS
} from './schedulerConstants';
import {
    extractTiming,
    getAssignmentKey,
    getCalendars,
    hasAssignmentEventResourceKey,
    hasAssignmentIdentifier,
    isAssignmentWithoutLineItemKey,
    getMergedStartEnd,
    getPlanningDependencyKey,
    getProjectCalendar,
    getReservationAssignmentsFromEvents,
    getUniqueAssignments,
    productionDataToBryntumFormat,
    productToBryntumResourcesAndCalendars,
    SchedulerLineItemFromBryntumEvent,
    SYSTEM_CALENDAR,
    toBryntumCalendars,
    toBryntumDependencies,
    toBryntumEventsAndAssignments,
    toBryntumEventsAndAssignmentsForActivity,
    toBryntumResourcesAndCalendars,
    toSchedulerActivityItemsFromProduction,
    toSchedulerLineItemFromBryntumEvent,
    toSchedulerLineItemsFromReservations,
    updateEventsForAutoScheduler,
    updateEventsForRenderingOnTimeline
} from './schedulerUtil';
import { DURATION_UNIT_MAPPING, SR_TYPES } from './timelineEventFormatting';
import { SchedulerLineItem, SchedulerResource } from './types';
import { assignmentIsForEvents, dependencyIsForEvents } from './timelineUpdates';

interface UpdatedEvent {
    calendar?: string | null; // change to your concrete type if known (e.g., string | Calendar)
    durationUnit?: string;
    duration?: number;
    dateRange?: DateRange; // or your existing DateRange type
}

type MoveProdResult = {
    updateDates: SchedulerLineItemFromBryntumEvent[];
    events: Model[];
};

export type TimelineFilterType = {
    displayType: string;
    value: string;
    label: string;
    name: string;
    isBoolean?: boolean;
    dontfetchData?: boolean;
};

const headlessProject = createSchedulerProject();

let calculating = false;
let initialized = false;
let initializedForOwnerId: string | null = null;
let calendarsLoadedForOwnerId: string | null = null;
let headlessInitializationPromise: Promise<void> | null = null;
let headlessInitializationOwnerId: string | null = null;

const normalizeOwnerId = (ownerId?: string | null) => ownerId ?? null;

const setOwnerIdTracking = (ownerId?: string | null) => {
    const normalizedOwnerId = normalizeOwnerId(ownerId);
    initializedForOwnerId = normalizedOwnerId;
    calendarsLoadedForOwnerId = normalizedOwnerId;
};

const resetHeadlessSchedulerInitialization = () => {
    initialized = false;
    setOwnerIdTracking(null);
    headlessInitializationPromise = null;
    headlessInitializationOwnerId = null;
};

export const dedupeRecordsById = <T extends { id?: string | number }>(
    records: Array<T | undefined | null> = []
): T[] => {
    const map = new Map<string | number, T>();

    for (const record of records) {
        if (!record) continue;
        if (record.id == null) continue;
        map.set(record.id, record);
    }

    return [...map.values()];
};

export const schedulerStore = 'schedulerData';
export const schedulerPreviewStore = 'schedulerPreviewData';
export const schedulerProductionStore = 'schedulerProductionData';

const buildHeadlessResourceRecords = (lineItems: SchedulerLineItem[] = []) =>
    dedupeRecordsById(
        lineItems.flatMap((lineItem) => {
            const assignedResourceNames = new Map(
                (lineItem.assignedResources || [])
                    .filter((resource) => resource?.id)
                    .map((resource) => [resource.id, resource.name])
            );

            return (lineItem.resources || []).filter(Boolean).map((resourceId) => ({
                id: resourceId,
                name:
                    assignedResourceNames.get(resourceId) ||
                    lineItem.attributes?.reservationForName ||
                    lineItem.name ||
                    String(resourceId)
            }));
        })
    );

const ensureHeadlessSchedulerResources = async (lineItems: SchedulerLineItem[] = []) => {
    const resourcesToEnsure = buildHeadlessResourceRecords(lineItems);

    if (!resourcesToEnsure.length) {
        return;
    }

    const existingResourceIds = new Set(
        headlessProject.resourceStore.records.map((resourceRecord) => String(resourceRecord.id))
    );
    const missingResources = resourcesToEnsure.filter((resource) => !existingResourceIds.has(String(resource.id)));

    if (missingResources.length) {
        headlessProject.resourceStore.add(missingResources);
        await headlessProject.commitAsync();
    }
};

export const TIMELINE_BOOKING_FILTERS = [
    {
        displayType: 'TextFacet',
        dontfetchData: true,
        value: 'opportunityName_booking',
        label: 'filter.bookingName',
        name: 'opportunityName_booking'
    },
    {
        displayType: 'CustomComboBoxFacet',
        value: 'organization_booking',
        label: 'filter.bookingOrganization',
        name: 'Organization'
    },
    {
        displayType: 'CustomComboBoxFacet',
        value: 'production_booking',
        label: 'filter.production',
        name: 'Production'
    },
    {
        displayType: 'CustomComboBoxFacet',
        value: 'ownerOrganizationName_booking',
        label: 'filter.ownerOrganization',
        name: 'Organization'
    },
    {
        displayType: 'CustomListFacet',
        value: 'status_booking',
        label: 'filter.status',
        name: 'ReservationStatus'
    },
    // {
    //     displayType: 'CustomComboBoxFacet',
    //     value: 'contact_booking',
    //     label: 'common.contact',
    //     name: 'Person'
    // },
    {
        displayType: 'CustomListFacet',
        value: 'opportunityType_booking',
        label: 'filter.bookingType',
        name: 'OpportunityType'
    },
    {
        displayType: 'CustomListFacet',
        isBoolean: true,
        value: 'primary_booking',
        label: 'filter.primary',
        name: 'primary'
    },
    {
        displayType: 'CustomListFacet',
        isBoolean: true,
        value: 'reservationGuestsAvailable_booking',
        label: 'filter.guests',
        name: 'reservationGuestsAvailable'
    },
    {
        displayType: 'CustomListFacet',
        isBoolean: true,
        value: 'isChild_booking',
        label: 'filter.onlyDetails',
        name: 'isChild'
    }
];

export const PRODUCT_ORG_FILTER = 'ownerOrganizationName_product';

export const TIMELINE_PRODUCT_FILTERS = [
    {
        displayType: 'CustomListFacet',
        value: 'type_product',
        label: 'filter.productType',
        name: 'CatalogType'
    },
    {
        displayType: 'CustomComboBoxFacet',
        dontfetchData: true,
        value: 'locations_product',
        label: 'filter.location',
        name: 'locations_product'
    },
    {
        displayType: 'CustomListFacet',
        value: 'subType_product',
        label: 'filter.subType',
        name: 'CatalogSubType'
    },
    {
        displayType: 'CustomListFacet',
        value: 'offerType_product',
        label: 'filter.offerType',
        name: 'OfferType'
    },
    {
        displayType: 'CustomComboBoxFacet',
        value: 'category_product',
        label: 'filter.category',
        name: 'Category'
    }
];

export const TIMELINE_PRODUCT_FILTERS_BY_VIEW = {
    'home-sched': [
        {
            displayType: 'CustomComboBoxFacet',
            dontfetchData: true,
            value: PRODUCT_ORG_FILTER,
            label: 'filter.ownerOrganizationName',
            name: 'Organization'
        },
        ...TIMELINE_PRODUCT_FILTERS
    ],
    'multiLocation-sched': TIMELINE_PRODUCT_FILTERS,
    'preview-vertical': [
        {
            displayType: 'CustomComboBoxFacet',
            value: 'name_product',
            label: 'filter.productName',
            name: 'Product'
        },
        {
            displayType: 'CustomComboBoxFacet',
            dontfetchData: true,
            value: PRODUCT_ORG_FILTER,
            label: 'filter.ownerOrganizationName',
            name: 'Organization'
        },
        ...TIMELINE_PRODUCT_FILTERS
    ]
};

export const PRODUCTION_DEFAULT_FILTERS = [
    { identifier: FILTER_IDENTIFIERS.managedProduction, value: 'true' },
    { identifier: FILTER_FIELD_NAMES.state, value: STATE_TYPE.managed }
];

const shouldReinitializeHeadlessScheduler = (ownerId: string) =>
    !initialized ||
    initializedForOwnerId !== ownerId ||
    calendarsLoadedForOwnerId !== ownerId ||
    !headlessProject.calendarManagerStore.data?.length;

const initializeHeadlessScheduler = async (ownerId: string, organizationAccountDetails) => {
    const calendarResponse = await getCalendars(ownerId);
    const calendars = toBryntumCalendars(calendarResponse?.data?.results?.hits?.items || []);

    await headlessProject.loadInlineData({
        events: [],
        resources: [],
        assignments: [],
        dependencies: []
    });
    headlessProject.calendars = calendars;
    headlessProject.calendar = getProjectCalendar(calendars, organizationAccountDetails)?.id || undefined;
    await headlessProject.commitAsync();
    initialized = true;
    setOwnerIdTracking(ownerId);
};

export const ensureHeadlessSchedulerReady = async (
    activeOrganizationAccount: string,
    organizationAccountDetails,
    lineItems: SchedulerLineItem[] = []
) => {
    const normalizedOwnerId = normalizeOwnerId(activeOrganizationAccount);

    if (!normalizedOwnerId) {
        throw new Error('active organization account is required');
    }

    while (headlessInitializationPromise) {
        await headlessInitializationPromise;
    }

    if (shouldReinitializeHeadlessScheduler(normalizedOwnerId)) {
        headlessInitializationOwnerId = normalizedOwnerId;
        headlessInitializationPromise = initializeHeadlessScheduler(
            normalizedOwnerId,
            organizationAccountDetails
        ).finally(() => {
            headlessInitializationPromise = null;
            headlessInitializationOwnerId = null;
        });

        await headlessInitializationPromise;
    }

    await ensureHeadlessSchedulerResources(lineItems);

    return headlessProject;
};

export const getDataFromIDB = async (activeOrganizationAccount, store: string) => {
    const dataFromIDB = await db
        .table('schedulerStores')
        .where('name')
        .equals(activeOrganizationAccount + `_${store}`)
        .toArray();

    if (!isEmpty(dataFromIDB)) {
        return dataFromIDB[0].value;
    }
    return dataFromIDB;
};

const updateDataToIDB = async (activeOrganizationAccount, store, data) => {
    db.table('schedulerStores')
        .where('name')
        .equals(activeOrganizationAccount + `_${store}`)
        .delete();
    db.table('schedulerStores').add({ name: activeOrganizationAccount + `_${store}`, value: data });
};

export const updateSchedulerData = async (
    store,
    lineItems: SchedulerLineItem[],
    activeOrganizationAccount,
    hasDependencies = false,
    updateDate = false
) => {
    const { events, assignments } = updateEventsForRenderingOnTimeline(lineItems);
    const { resources, calendars } = toBryntumResourcesAndCalendars(lineItems);
    const dataFromIDB = await db
        .table('schedulerStores')
        .where('name')
        .equals(activeOrganizationAccount + `_${store}`)
        .toArray();
    if (!isEmpty(dataFromIDB) && dataFromIDB[0].value) {
        const data = dataFromIDB[0].value;
        data.resources.forEach((res) => {
            remove(resources, (r) => r.id === res.id);
        });
        data.calendars = data.calendars.map((cal) => {
            const nc = calendars.find((c) => c.id === cal.id);
            if (nc) {
                cal.intervals?.push(...(nc.intervals ?? []));
                remove(calendars, (c) => c.id === cal.id);
            }
            return cal;
        });
        events.forEach((evt) => {
            remove(data.events, (e: any) => !e || e.lineItemId === evt.id);
            remove(data.assignments, (a: any) => !a || a.lineItemId === evt.id);
        });

        data.events = [...data.events, ...events];
        data.assignments = [...data.assignments, ...assignments];
        if (updateDate) {
            data.lastFetchedAt = new Date();
        }
        updateDataToIDB(activeOrganizationAccount, store, data);
        resetHeadlessSchedulerInitialization();
    }
};

export const updateResourceData = (activeOrganizationAccount) => {
    getDataFromIDB(activeOrganizationAccount, schedulerPreviewStore).then((data) => {
        if (!isEmpty(data)) {
            getProductsData(activeOrganizationAccount).then((res) => {
                const products = res?.data?.results?.hits?.items;
                const { resources } = productToBryntumResourcesAndCalendars(products);
                data.resources = resources;
                updateDataToIDB(activeOrganizationAccount, schedulerPreviewStore, data);
            });
        }
    });
};

export const deleteResourceData = async (resourceID, activeOrganizationAccount) => {
    getDataFromIDB(activeOrganizationAccount, schedulerPreviewStore).then((data) => {
        if (!isEmpty(data)) {
            remove(data.resources, (r: SchedulerResource) => {
                return r.id === resourceID;
            });
            updateDataToIDB(activeOrganizationAccount, schedulerPreviewStore, data);
        }
    });
};

export const deleteLinesFromSchedulerData = async (eventsToDelete, activeOrganizationAccount) => {
    const stores = [schedulerPreviewStore, schedulerStore];

    stores.map((store) => {
        getDataFromIDB(activeOrganizationAccount, store).then((data) => {
            if (!isEmpty(data)) {
                eventsToDelete.forEach((eventID) => {
                    remove(data.events, (e: any) => e.lineItemId === eventID);
                    remove(data.assignments, (a: any) => a.lineItemId === eventID);
                });

                updateDataToIDB(activeOrganizationAccount, store, data);
            }
        });
    });
};

export const deleteBookingFromSchedulerData = async (opportunityId: string, activeOrganizationAccount) => {
    getDataFromIDB(activeOrganizationAccount, schedulerPreviewStore).then((data) => {
        if (!isEmpty(data)) {
            const eventsToDelete = [];
            remove(data.events, (e: any) => {
                if (e.id === opportunityId) eventsToDelete.push(e.id);
                return e.opportunityId === opportunityId;
            });
            remove(data.assignments, (a: any) => eventsToDelete.includes(a.eventId));
            updateDataToIDB(activeOrganizationAccount, schedulerPreviewStore, data);
        }
    });
};

export const refreshSchedulerDataAttributes = async (opportunity, activeOrganizationAccount) => {
    const attributes = opportunity?.[0]?.attributes;

    if (!attributes?.opportunityId) {
        return;
    }

    getDataFromIDB(activeOrganizationAccount, schedulerPreviewStore).then((data) => {
        if (!isEmpty(data)) {
            data.events?.forEach((event) => {
                if (event?.attributes?.opportunityId === attributes.opportunityId) {
                    event.status = attributes.status;
                    event.attributes.name = attributes.name;
                    event.attributes.contact = attributes.contact;
                    event.attributes.opportunityName = attributes.opportunityName;
                    event.attributes.organizationName = attributes.organizationName;
                    event.attributes.production = attributes.production;
                    event.attributes.organization = attributes.organization;
                    event.attributes.opportunityType = attributes.opportunityType;
                    event.attributes.contact = attributes.contact;
                    event.attributes.status = attributes.status;
                }
            });
            updateDataToIDB(activeOrganizationAccount, schedulerPreviewStore, data);
        }
    });
};

type EventDates = {
    startDate?: string;
    durationUnit?: string;
    endDate?: string;
    duration?: number;
    calendar?: string;
    dateRange?: DateRange;
};

export const scheduleItemsForMoveProduction = async (
    activityEvents = [],
    customDependencies = [],
    stm: StateTrackingManager,
    scheduler: () => SchedulerPro,
    application: AppDefinition,
    locale
): Promise<MoveProdResult> => {
    if (!Array.isArray(activityEvents) || activityEvents.length === 0) {
        calculating = false;
        return { updateDates: [], events: [] };
    }
    stm.startTransaction(MOVE_SCHEDULER_NAME.production);
    try {
        calculating = true;
        const project = scheduler().project;
        const { eventStore, dependencyStore } = project;
        const { events = [] } = toBryntumEventsAndAssignmentsForActivity(activityEvents, application);
        await project.commitAsync();
        events.filter(Boolean).forEach((event) => {
            event.manuallyScheduled = false;
        });

        const tempActivityEvent = events.find((e) => e?.name === TEMP_NAME.activity);
        if (tempActivityEvent && !eventStore.getById(tempActivityEvent.identity.id)) {
            eventStore.add(tempActivityEvent);
        }
        if (customDependencies.length > 0) {
            dependencyStore.add(customDependencies);
        } else {
            const depsToRemove = activityEvents.flatMap((li) => {
                const event = eventStore.getById(li?.identity?.id || li?.id);
                return event ? dependencyStore.getEventDependencies(event) : [];
            });
            dependencyStore.remove(depsToRemove);
        }

        const updatedEvents = await Promise.all(
            activityEvents.map(async (activity) => {
                if (!activity) return null;
                const activityMeta = extractTiming(activity);
                if (!activityMeta.activityId) return null;
                const eventRecord = eventStore.getById(activityMeta.activityId);
                if (!eventRecord) return null;
                let updatedEvent: UpdatedEvent = {
                    calendar: activityMeta.calendar,
                    durationUnit: activityMeta.durationUnit,
                    duration: activityMeta.duration,
                    dateRange: activity.dateRange
                };

                const isAutoScheduled =
                    customDependencies.some((dep) => dep.to === activityMeta.activityId) &&
                    activityMeta.activityId !== CONSTANTS_ID.newEntityId;

                if (isAutoScheduled) {
                    updatedEvent.manuallyScheduled = false;
                    await eventRecord.setManuallyScheduled(false);
                    const parentEvent = eventStore.getById(activityMeta.activityId);
                    if (parentEvent?.startDate) {
                        updatedEvent = {
                            startDate: parentEvent.startDate,
                            duration: activityMeta.duration,
                            calendar: activityMeta.calendar,
                            durationUnit: activityMeta.durationUnit
                        };
                    }
                    if (!updatedEvent.startDate) {
                        await eventRecord.setStartDate(null);
                    }
                } else {
                    if (activityMeta.durationSpecified) {
                        updatedEvent.dateRange.dateTimeDuration.value = activityMeta.duration;
                    } else if (activityMeta.endValid) {
                        updatedEvent.dateRange.end = toDateTimeISOWithoutOffset(
                            activityMeta.isHourly
                                ? activityMeta.mergedEnd
                                : DateHelper.add(activityMeta.mergedEnd, 1, TIME_CONSTANTS.d)
                        );
                    }

                    if (activityMeta.startValid) {
                        updatedEvent.dateRange.start = toDateTimeISOWithoutOffset(activityMeta.mergedStart);
                    }
                }
                eventRecord.style = 'font-style: italic;';
                await eventRecord.setAsync(updatedEvent);
                return eventRecord;
            })
        );

        await project.commitAsync();
        const eventToRemove = eventStore.getById(CONSTANTS_ID.newEntityId);
        if (eventToRemove) {
            eventStore.remove(eventToRemove);
        }
        calculating = false;
        const eventsOut = (updatedEvents ?? []).filter((e): e is Model => !!e && e.id !== CONSTANTS_ID.newEntityId);
        const updateDates = toSchedulerLineItemFromBryntumEvent(eventsOut, project, locale, {
            movingProduction: true
        });
        return { updateDates, events: eventsOut };
    } catch (error) {
        log('Error scheduling items for move activity:', error);
        calculating = false;
        return { updateDates: [], events: [] };
    } finally {
        stm.stopTransaction(MOVE_SCHEDULER_NAME.production);
    }
};

export const scheduleItemsForMoveBooking = async (lineItems = [], customDependencies = [], locale) => {
    if (!Array.isArray(lineItems) || lineItems.length === 0) {
        calculating = false;
        return [];
    }

    if (!initialized) {
        throw new Error('scheduler is not initialized');
    }

    if (calculating) {
        throw new Error('scheduler is busy');
    }

    try {
        calculating = true;
        const groupedChildren: Record<string, SchedulerLineItem[]> = {};
        const autoSequencedDependencies = [];

        for (const li of lineItems) {
            const parentId = li?.attributes?.parent?.reference?.id;
            if (li?.attributes?.isChild && parentId) {
                groupedChildren[parentId] = groupedChildren[parentId] || [];
                groupedChildren[parentId].push(li);
            }
        }

        Object.values(groupedChildren).forEach((children) => {
            if (children.length > 1) {
                autoSequencedDependencies.push(...toBryntumDependencies(children));
            }
        });

        const hasCustomDeps = Array.isArray(customDependencies) && customDependencies.length > 0;
        const allDependencies = hasCustomDeps ? customDependencies : autoSequencedDependencies;
        const { events = [] } = updateEventsForAutoScheduler(lineItems, allDependencies.length > 0, {
            treatEndAsInclusive: true
        });
        const eventsById = keyBy(events, 'id');
        await headlessProject.commitAsync();
        events.filter(Boolean).forEach((event) => {
            event.manuallyScheduled = false;
        });

        const dependencyStore = headlessProject.dependencyStore;
        const eventStore = headlessProject.eventStore;
        const bookingEvent = events.find((e) => e?.eventType === ENTITY_CONSTANTS.booking.toLowerCase());
        if (bookingEvent && !eventStore.getById(bookingEvent.id)) {
            eventStore.add([bookingEvent]);
        }
        eventStore.add(events);
        if (allDependencies.length > 0) {
            dependencyStore.add(allDependencies);
        } else {
            const depsToRemove = lineItems.flatMap((li) => {
                const event = eventStore.getById(li?.id) as EventModel;
                return event ? dependencyStore.getEventDependencies(event) : [];
            });
            dependencyStore.remove(depsToRemove);
        }

        await headlessProject.commitAsync();

        const updatedEvents = await Promise.all(
            lineItems.map(async (lineItem) => {
                const eventRecord = eventStore.getById(lineItem?.id) as EventModel;
                if (!eventRecord) return null;
                const { mergedStart, mergedEnd } = getMergedStartEnd(lineItem.dateRange);
                const startValid = isValid(mergedStart);
                const endValid = isValid(mergedEnd);
                const isHourly = lineItem.dateRange?.dateTimeDuration?.unit === TIME_CONSTANTS.hour;
                let updatedEvent: any = {
                    calendar: lineItem.calendar,
                    durationUnit: DURATION_UNIT_MAPPING.get(lineItem.dateRange?.dateTimeDuration?.unit),
                    dateRange: lineItem.dateRange
                };

                const hasIncomingDep = allDependencies.some((dep) => dep.to === lineItem.id);
                const parentId = lineItem.attributes?.parent?.reference?.id;
                const isChild = !!lineItem.attributes?.isChild;
                const isFirstChild = !!eventsById[lineItem.id]?.attributes?.isFirstChild;

                if (hasIncomingDep && !parentId && !isChild) {
                    eventRecord.manuallyScheduled = false;
                    return eventRecord;
                }
                const isAutoScheduled = hasIncomingDep || isFirstChild;

                if (isAutoScheduled) {
                    updatedEvent.manuallyScheduled = false;
                    eventRecord.manuallyScheduled = false;
                    if (isFirstChild && parentId) {
                        const parentEvent = eventStore.getById(parentId) as EventModel;
                        if (parentEvent?.startDate) {
                            updatedEvent.startDate = parentEvent.startDate;
                            updatedEvent.manuallyScheduled = true;
                            eventRecord.manuallyScheduled = true;
                        }
                    } else if (parentId) {
                        const parentEvent = eventStore.getById(parentId) as EventModel;
                        if (parentEvent?.startDate) {
                            updatedEvent = {
                                startDate: parentEvent.startDate,
                                duration: lineItem.dateRange?.dateTimeDuration?.value,
                                calendar: lineItem.calendar,
                                durationUnit: DURATION_UNIT_MAPPING.get(lineItem.dateRange?.dateTimeDuration?.unit)
                            };
                        }
                    }

                    if (!updatedEvent.startDate) {
                        await eventRecord.setStartDate(null);
                    }
                } else {
                    if (lineItem.dateRange?.durationSpecified) {
                        updatedEvent.dateRange.dateTimeDuration.value = lineItem.dateRange.dateTimeDuration?.value;
                    } else if (endValid) {
                        updatedEvent.dateRange.end = isHourly
                            ? toDateTimeISOWithoutOffset(mergedEnd)
                            : toDateTimeISOWithoutOffset(DateHelper.add(mergedEnd, 1, TIME_CONSTANTS.d));
                    }

                    if (startValid) {
                        updatedEvent.dateRange.start = toDateTimeISOWithoutOffset(mergedStart);
                    }
                }
                await eventRecord.setAsync(updatedEvent);
                return eventRecord;
            })
        );

        await headlessProject.commitAsync();
        const eventToRemove = eventStore.getById(CONSTANTS_ID.newEntityId);
        if (eventToRemove) {
            eventStore.remove(eventToRemove);
        }
        calculating = false;
        return toSchedulerLineItemFromBryntumEvent(updatedEvents ?? [], headlessProject, locale);
    } catch (error) {
        log('Error scheduling items for move booking:', error);
        calculating = false;
        return [];
    }
};

/**
 * Schedules line items in the Bryntum scheduler, optionally sequencing them and managing dependencies.
 *
 * - Adds new events and assignments to the scheduler if they do not exist.
 * - Handles dependencies between events based on the `sequenceItems` flag.
 * - Updates event dates, durations, calendars, and resource assignments according to the provided line items.
 * - Commits all changes asynchronously to the scheduler project.
 * - Returns the updated scheduler line items formatted for UI consumption.
 *
 * @param lineItems - Array of scheduler line items to be scheduled.
 * @param sequenceItems - If true, events are sequenced with dependencies; if false, dependencies are removed.
 * @param id - The unique identifier for the event group to be scheduled.
 * @param locale - The locale used for formatting returned scheduler line items.
 * @returns A promise that resolves to the updated scheduler line items.
 * @throws If the scheduler is not initialized or is currently busy.
 */
export const scheduleItems = async (
    lineItems: SchedulerLineItem[],
    sequenceItems = false,
    id: string,
    locale: string
) => {
    if (!initialized) {
        throw new Error('scheduler is not initialized');
    }

    if (calculating) {
        throw new Error('scheduler is busy');
    }
    calculating = true;
    try {
        const { events, assignments } = updateEventsForAutoScheduler(lineItems, sequenceItems);
        let record = headlessProject.eventStore.getById(id) as EventModel;
        // add event if it doesn't exist
        if (!record) {
            headlessProject.eventStore.add(events);
            headlessProject.assignmentStore.add(assignments);
            if (sequenceItems) {
                const dependencies = toBryntumDependencies(lineItems);
                headlessProject.dependencyStore.add(dependencies);
            }
            await headlessProject.commitAsync();
            record = headlessProject.eventStore.getById(id) as EventModel;
        }
        // add/remove dependencies if applicable
        const dependencyRecords = headlessProject.dependencyStore.getEventDependencies(record);
        if (dependencyRecords.length === 0) {
            if (sequenceItems) {
                await Promise.all(
                    lineItems.map(async (li, idx) => {
                        const event = headlessProject.eventStore.getById(li.id) as EventModel;
                        if (idx !== 0) {
                            await event.setAsync({ manuallyScheduled: false });
                        }
                    })
                );
                const dependencies = toBryntumDependencies(lineItems);
                headlessProject.dependencyStore.add(dependencies);
            }
        } else {
            if (!sequenceItems) {
                await Promise.all(
                    lineItems.map(async (li) => {
                        const event = headlessProject.eventStore.getById(li.id) as EventModel;
                        await event.setAsync({ manuallyScheduled: true });
                        dependencyRecords.push(...headlessProject.dependencyStore.getEventDependencies(event));
                    })
                );

                headlessProject.dependencyStore.remove(dependencyRecords);
            }
        }
        await headlessProject.commitAsync();
        const updatedEvents = await Promise.all(
            lineItems.map(async (li, idx: number) => {
                const eventRecord = headlessProject.eventStore.getById(li.id) as EventModel;
                const { mergedStart, mergedEnd } = getMergedStartEnd(li.dateRange);
                const isStartValid = isValid(mergedStart);
                const isEndValid = isValid(mergedEnd);
                const isHourly = li.dateRange.dateTimeDuration?.unit === TIME_UNITS.hour;
                const isDurationMode = !!li.dateRange.durationSpecified;

                const resources = li.resources.map((r) => headlessProject.resourceStore.getById(r));
                const resourceChanges = ArrayHelper.delta(resources, eventRecord.resources);
                let updatedEvent: EventDates;
                if (isDurationMode) {
                    // Duration mode: start + duration + calendar
                    updatedEvent = {
                        startDate: isStartValid ? toDateTimeISOWithoutOffset(mergedStart) : undefined,
                        duration: li.dateRange.dateTimeDuration?.value,
                        durationUnit: DURATION_UNIT_MAPPING.get(li.dateRange.dateTimeDuration?.unit),
                        calendar: li.calendar
                    };
                    updatedEvent.dateRange = li.dateRange;
                    // For sequenceItems, dependents might be duration-only.
                    if (sequenceItems && idx !== 0) {
                        updatedEvent = {
                            duration: li.dateRange.dateTimeDuration?.value,
                            durationUnit: DURATION_UNIT_MAPPING.get(li.dateRange.dateTimeDuration?.unit),
                            calendar: li.calendar
                        };
                    }
                } else {
                    // Date-range mode: start + end + calendar; duration derived
                    updatedEvent = {
                        startDate: isStartValid ? toDateTimeISOWithoutOffset(mergedStart) : undefined,
                        durationUnit: DURATION_UNIT_MAPPING.get(li.dateRange.dateTimeDuration?.unit),
                        calendar: li.calendar
                    };
                    updatedEvent.dateRange = li.dateRange;
                    if (isEndValid) {
                        if (isHourly) {
                            // Hour mode: exact end, no +1 day
                            updatedEvent.endDate = toDateTimeISOWithoutOffset(mergedEnd);
                        } else {
                            let finalEnd = mergedEnd;
                            if (isStartValid && isEndValid && isAtLeast24hoursApart(mergedStart, mergedEnd)) {
                                finalEnd = DateHelper.add(mergedEnd, 1, TIME_CONSTANTS.d);
                            }
                            updatedEvent.endDate = toDateTimeISOWithoutOffset(finalEnd);
                        }
                    } else {
                        updatedEvent.endDate = undefined;
                    }
                }
                await eventRecord.setAsync(updatedEvent);
                if (resourceChanges?.toAdd?.length > 0 || resourceChanges?.toRemove?.length > 0) {
                    headlessProject.eventStore.unassignEventFromResource(eventRecord, resourceChanges.toRemove);
                    headlessProject.eventStore.assignEventToResource(eventRecord, resourceChanges.toAdd);
                }
                return eventRecord;
            })
        );

        return toSchedulerLineItemFromBryntumEvent(updatedEvents ?? [], headlessProject, locale);
    } finally {
        // ensure we always reset the flag even if something throws
        calculating = false;
    }
};

export const onCancelEvent = () => {
    headlessProject.eventStore.revertChanges();
    headlessProject.dependencyStore.revertChanges();
    headlessProject.assignmentStore.revertChanges();
};

export const onSaveEvent = async () => {
    await headlessProject.commitAsync();
};

export const PRODUCT_DEFAULT_FILTERS = [
    { identifier: FILTER_IDENTIFIERS.inactive, value: 'false' },
    { identifier: FILTER_IDENTIFIERS.reservable, value: 'true' }
];
export const getProductsData = (activeOrganizationAccount) => {
    return searchClient.query({
        query: slimResultsQuery('Product'),
        variables: {
            filters: [{ identifier: 'entity', value: 'Product' }, ...PRODUCT_DEFAULT_FILTERS],
            page: {
                from: 0,
                size: 1000
            }
        },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        }
    });
};

export const getResourceData = (activeOrganizationAccount) => {
    return searchClient.query({
        query: slimResultsQuery('Resource'),
        variables: {
            filters: [{ identifier: 'entity', value: 'Resource' }],
            page: {
                from: 0,
                size: 1000
            }
        },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        }
    });
};

export const getProductionData = (activeOrganizationAccount) => {
    return searchClient.query({
        query: slimResultsQuery('Production'),
        variables: {
            filters: [{ identifier: 'entity', value: 'Production' }, ...PRODUCTION_DEFAULT_FILTERS],
            page: {
                from: 0,
                size: 1000
            }
        },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        }
    });
};

export const getSchedulerData = async (activeOrganizationAccount) => {
    let data = await db
        .table('schedulerStores')
        .where('name')
        .equals(activeOrganizationAccount + `_${schedulerPreviewStore}`)
        .toArray();

    return new Promise((resolve, reject) => {
        if (!isEmpty(data)) {
            resolve(data[0].value);
        } else {
            getProductsData(activeOrganizationAccount).then((res) => {
                const products = res?.data?.results?.hits?.items;
                searchClient
                    .query({
                        query: slimResultsQuery('Reservation'),
                        variables: {
                            filters: [{ identifier: 'entity', value: 'Reservation' }],
                            page: {
                                from: 0,
                                size: 2000
                            }
                        },
                        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
                        context: {
                            headers: {
                                ownerId: activeOrganizationAccount
                            }
                        }
                    })
                    .then((res) => {
                        const schedulerLineItems = toSchedulerLineItemsFromReservations(
                            res?.data?.results?.hits?.items ?? []
                        );
                        const { resources, calendars } = productToBryntumResourcesAndCalendars(products);
                        const { events, assignments } = toBryntumEventsAndAssignments(schedulerLineItems);
                        const data = {
                            events: events,
                            resources: resources,
                            assignments: assignments,
                            calendars: calendars
                        };
                        db.table('schedulerStores').add({
                            name: activeOrganizationAccount + `_${schedulerPreviewStore}`,
                            value: data
                        });
                        resolve(data);
                    });
            });
        }
    });
};

export const getFilterData = async (
    activeOrganizationAccount: string,
    TIMELINE_FILTERS: TimelineFilterType[],
    t: (token: string, values?: SubstitutionMapType) => string
) => {
    const data = {
        results: {
            facets: [],
            hits: {
                page: {
                    total: 0
                }
            }
        }
    };

    const promises = TIMELINE_FILTERS.map(async (filter) => {
        const entriesArr = [];
        const filters = [{ identifier: 'entity', value: filter.name }];

        if (filter.name === 'Product') {
            filters.push(...PRODUCT_DEFAULT_FILTERS);
        }
        if (!filter.isBoolean && !filter.dontfetchData) {
            const res = await searchClient.query({
                query: slimResultsQuery(filter.name),
                variables: {
                    filters: filters,
                    page: {
                        from: 0,
                        size: 1000
                    }
                },
                fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
                context: {
                    headers: {
                        ownerId: activeOrganizationAccount
                    }
                }
            });
            const filterData = res?.data?.results?.hits?.items;
            filterData.map((f) => {
                entriesArr.push({
                    id: filter.name + '_' + f.name,
                    count: 1,
                    label: f.name,
                    __typename: 'SKFacetSetEntry'
                });
            });
        } else if (filter.isBoolean) {
            entriesArr.push({
                id: filter.name + '_' + true,
                count: 1,
                label: true,
                __typename: 'SKFacetSetEntry'
            });
        }

        return {
            identifier: filter.value,
            type: 'RefinementSelectFacet',
            label: t(filter.label),
            display: filter.displayType,
            entries: entriesArr
        };
    });

    const results = await Promise.all(promises);
    results.forEach((result) => {
        data.results.facets.push(result);
    });

    return data;
};

export const toBryntumFormat = (resdata, products, calendar) => {
    const schedulerLineItems = toSchedulerLineItemsFromReservations(resdata ?? []);
    const { resources, calendars } = productToBryntumResourcesAndCalendars(products);
    const { events, assignments } = updateEventsForRenderingOnTimeline(schedulerLineItems);
    // filter out old data where startDate is after EndDate
    const validEvents = events.filter((event) => {
        const { mergedStart, mergedEnd } = getMergedStartEnd(event.dateRange);
        return !isAfter(mergedStart, mergedEnd);
    });
    const data = {
        events: validEvents,
        resources: resources,
        assignments: assignments,
        calendars: [...calendars, ...(calendar || [])],
        lastFetchedAt: new Date(),
        eventsFetchedAt: new Date()
    };
    return data;
};

export const fetchReservationData = async (activeOrganizationAccount, from, batchSize, filter) => {
    try {
        let filters = [{ identifier: 'entity', value: 'Reservation' }];
        filters.push(filter);
        const initialResponse = await searchClient.query({
            query: slimResultsQuery('Reservation'),
            variables: {
                filters: filters,
                page: {
                    from: from,
                    size: batchSize
                }
            },
            fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
            context: {
                headers: {
                    ownerId: activeOrganizationAccount
                }
            }
        });
        return initialResponse;
    } catch (error) {
        console.error('Error fetching data:', error);
    }
};

export const UpdateIDB = async (activeOrganizationAccount, store, data, replaceEventIds: string[] = []) => {
    // Keep SSE edits, refreshes, and initial-load read/merge/write operations atomic in both apps.
    await db.transaction('rw', db.table(SCHEDULER_INDEX_DB_TABLE.schedulerStores), async () => {
        await updateDataInIdxDB(activeOrganizationAccount, store, data, replaceEventIds);
    });
};

export const getCachedData = async (activeOrganizationAccount, storeName) => {
    let data = await db
        .table('schedulerStores')
        .where('name')
        .equals(activeOrganizationAccount + `_${storeName}`)
        .toArray();

    return !isEmpty(data) ? data[0].value : {};
};

// initializes calendar store if it is not already initialized.
export const initializeBryntumCalendars = async (activeOrganizationAccount) => {
    if (!headlessProject.calendarManagerStore.data?.length || calendarsLoadedForOwnerId !== activeOrganizationAccount) {
        const calendarData = await getCalendars(activeOrganizationAccount);
        if (calendarData?.data) {
            const bryntumCalendarData = toBryntumCalendars(calendarData.data.results?.hits?.items);
            headlessProject.calendars = bryntumCalendarData;
            calendarsLoadedForOwnerId = normalizeOwnerId(activeOrganizationAccount);
        }
    }
};

export const getCalendar = (calendarId): CalendarModel => {
    const calendarManagerStore = headlessProject.calendarManagerStore;
    return calendarId
        ? (calendarManagerStore.getById(calendarId) as CalendarModel)
        : (calendarManagerStore.findRecord('name', SYSTEM_CALENDAR) as CalendarModel);
};

export const computeDurationForDateRange = (dateRange: DateRange, calendarId: string, locale: string) => {
    const dRange = cloneDeep(dateRange);
    const { start, end, mergedStart, mergedEnd, dateTimeDuration } = destructureSchedule(dRange);
    const lineCal: CalendarModel = getCalendar(calendarId);
    if (lineCal && isValid(mergedStart) && isValid(mergedEnd) && dateTimeDuration.unit) {
        const durationInMS = lineCal.calculateDurationMs(
            dateTimeDuration.unit == TIME_UNITS.hour ? mergedStart : start,
            dateTimeDuration.unit == TIME_UNITS.hour || dateTimeDuration.value === 0 ? mergedEnd : addDays(end, 1)
        );
        dRange.dateTimeDuration.value = DateHelper.as(dRange.dateTimeDuration.unit, durationInMS, 'ms');
    }
    dRange.dateTimeDuration.value = roundUpByDurationUnit(
        dRange.dateTimeDuration.unit,
        dRange.dateTimeDuration.value,
        locale
    );
    return dRange;
};

export const getEventAllocationData = async (activeOrganizationAccount, lineItem) => {
    const dateRange = destructureSchedule(lineItem.dateRange);
    const dateBuckets = breakDateRangeIntoMonths(
        dateRange.mergedStart,
        dateRange.mergedEnd,
        lineItem.dateRange.dateTimeDuration.unit
    );

    await initializeBryntumCalendars(activeOrganizationAccount);
    const allocationData = dateBuckets.map((bucket) => {
        const lineCal = getCalendar(lineItem.calendar?.id);
        const workingMsEvent = lineCal.calculateDurationMs(
            bucket[0],
            lineItem['dateRange'].dateTimeDuration.unit == TIME_UNITS.hour ||
                lineItem['dateRange'].dateTimeDuration.value === 0
                ? bucket[1]
                : addDays(bucket[1], 1)
        );
        const allocationDataUnit =
            lineItem['dateRange'].dateTimeDuration.unit === TIME_UNITS.hour ? TIME_UNITS.hour : TIME_UNITS.day;
        return {
            startDate: format(bucket[0], API_DATE_FORMAT),
            endDate: format(bucket[1], API_DATE_FORMAT),
            periodUnit: 'Month',
            unit: UTILIZATION_UNITS_MAPPING.get(allocationDataUnit),
            value: DateHelper.as(allocationDataUnit, workingMsEvent, 'ms')
        };
    });
    return allocationData;
};

const updateDataToIDBAndIsSchedulerProductionRefetch = async (
    activeOrganizationAccount,
    data,
    setIsSchedulerProductionRefetchNeeded
) => {
    await db
        .table('schedulerStores')
        .where('name')
        .equals(activeOrganizationAccount + `_${schedulerProductionStore}`)
        .delete();
    await db
        .table('schedulerStores')
        .add({ name: activeOrganizationAccount + `_${schedulerProductionStore}`, value: data });
    setIsSchedulerProductionRefetchNeeded((prev) => !prev);
};

const updateDataToIDBAndIsSchedulerReservationRefetch = async (
    activeOrganizationAccount,
    data,
    setIsSchedulerReservationRefetchNeeded,
    isSchedulerReservationRefetchNeeded
) => {
    try {
        await db
            .table('schedulerStores')
            .where('name')
            .equals(activeOrganizationAccount + `_${schedulerPreviewStore}`)
            .delete();
        await db.table('schedulerStores').add({
            name: activeOrganizationAccount + `_${schedulerPreviewStore}`,
            value: data
        });
        const newIsSchedulerRefetchNeeded: boolean = !isSchedulerReservationRefetchNeeded;
        setIsSchedulerReservationRefetchNeeded(newIsSchedulerRefetchNeeded);
        localStorage.setItem('isSchedulerReservationRefetchNeeded', newIsSchedulerRefetchNeeded.toString());
    } catch (error) {
        console.warn('Error updating IndexedDB:', error);
    }
};

export const updateRecordInIdxDB = async (activeOrganizationAccount, data, storeName) => {
    try {
        const store = db.table('schedulerStores');
        const keyName = 'name';
        const record = await store
            .where(keyName)
            .equals(activeOrganizationAccount + `_${storeName}`)
            .first();
        if (record?.value) {
            const { events = [], assignments = [] } = record.value;
            await store.update(record.id, {
                value: {
                    ...record.value,
                    events: [...events, data.event],
                    assignments: [...assignments, ...data.assignments]
                }
            });
        }
    } catch (error) {
        console.warn('Error updating IndexedDB:', error);
    }
};

export const updateDataInIdxDB = async (activeOrganizationAccount, storeName, data, replaceEventIds: string[] = []) => {
    const storeKey = `${activeOrganizationAccount}_${storeName}`;
    const store = db.table(SCHEDULER_INDEX_DB_TABLE.schedulerStores);

    try {
        const record = await store.where(SCHEDULER_STORE_KEY.name).equals(storeKey).first();

        const incomingEvents = Array.isArray(data?.events) ? data.events : [];
        const incomingAssignmentsRaw = Array.isArray(data?.assignments) ? data.assignments : [];
        const incomingDependencies = Array.isArray(data?.dependencies) ? data.dependencies : [];
        const incomingResources = Array.isArray(data?.resources) ? data.resources : [];
        const incomingCalendars = Array.isArray(data?.calendars) ? data.calendars : [];
        const incomingAssignments = incomingAssignmentsRaw.filter(Boolean);

        if (record?.value) {
            const replacedIds = new Set(replaceEventIds);
            const existingEvents = (Array.isArray(record.value?.events) ? record.value.events : []).filter(
                (event) => !replacedIds.has(event?.id)
            );
            const existingAssignmentsRaw = Array.isArray(record.value?.assignments) ? record.value.assignments : [];
            const existingDependencies = (
                Array.isArray(record.value?.dependencies) ? record.value.dependencies : []
            ).filter((dependency) => data?.dependencies === undefined || !dependencyIsForEvents(dependency, replacedIds));
            const existingResources = Array.isArray(record.value?.resources) ? record.value.resources : [];
            const existingCalendars = Array.isArray(record.value?.calendars) ? record.value.calendars : [];
            const existingAssignments = existingAssignmentsRaw.filter(
                (assignment) => assignment && !assignmentIsForEvents(assignment, replacedIds)
            );

            const updatedEvents = dedupeRecordsById([...existingEvents, ...incomingEvents]);
            const allAssignments = [...incomingAssignments, ...existingAssignments];
            const schedulerAssignments = getUniqueAssignments(
                [...getReservationAssignmentsFromEvents(updatedEvents), ...allAssignments],
                (a) => Boolean(getAssignmentKey(a)),
                (a) => getAssignmentKey(a)
            );
            const updatedResources = dedupeRecordsById([...existingResources, ...incomingResources]);
            const updatedCalendars = dedupeRecordsById([...existingCalendars, ...incomingCalendars]);

            const filteredAssignmentsForActivitiesProduction = getUniqueAssignments(
                allAssignments,
                hasAssignmentEventResourceKey,
                (a) => getAssignmentKey(a)
            );

            const filteredAssignmentsForReservation = getUniqueAssignments(
                schedulerAssignments,
                (a) => hasAssignmentIdentifier(a?.lineItemId),
                (a) => getAssignmentKey(a) ?? a.lineItemId
            );

            const filteredAssignmentsForActivities = getUniqueAssignments(
                schedulerAssignments,
                (a) => isAssignmentWithoutLineItemKey(a) && hasAssignmentEventResourceKey(a),
                (a) => getAssignmentKey(a)
            );

            const filteredAssignments =
                storeName === SCHEDULER_INDEX_DB_TABLE.schedulerProductionData
                    ? filteredAssignmentsForActivitiesProduction
                    : [...filteredAssignmentsForReservation, ...filteredAssignmentsForActivities];

            const updatedDependencies = uniqBy(
                [...incomingDependencies, ...existingDependencies],
                (dependency) => getPlanningDependencyKey(dependency) ?? dependency
            );

            await store.update(record.id, {
                value: {
                    ...record.value,
                    ...data,
                    events: updatedEvents,
                    resources: updatedResources,
                    assignments: filteredAssignments,
                    dependencies: updatedDependencies,
                    calendars: updatedCalendars
                }
            });
        } else {
            const filteredAssignmentsForActivitiesProduction = getUniqueAssignments(
                incomingAssignments,
                hasAssignmentEventResourceKey,
                (a) => getAssignmentKey(a)
            );
            const schedulerAssignments = getUniqueAssignments(
                [...getReservationAssignmentsFromEvents(incomingEvents), ...incomingAssignments],
                (a) => Boolean(getAssignmentKey(a)),
                (a) => getAssignmentKey(a)
            );

            const filteredAssignmentsForReservation = getUniqueAssignments(
                schedulerAssignments,
                (a) => hasAssignmentIdentifier(a?.lineItemId),
                (a) => getAssignmentKey(a) ?? a.lineItemId
            );

            const filteredAssignmentsForActivities = getUniqueAssignments(
                schedulerAssignments,
                (a) => isAssignmentWithoutLineItemKey(a) && hasAssignmentEventResourceKey(a),
                (a) => getAssignmentKey(a)
            );

            const filteredAssignments =
                storeName === SCHEDULER_INDEX_DB_TABLE.schedulerProductionData
                    ? filteredAssignmentsForActivitiesProduction
                    : [...filteredAssignmentsForReservation, ...filteredAssignmentsForActivities];

            await store.add({
                name: storeKey,
                value: {
                    ...data,
                    events: dedupeRecordsById(incomingEvents),
                    resources: dedupeRecordsById(incomingResources),
                    assignments: filteredAssignments,
                    dependencies: uniqBy(
                        incomingDependencies,
                        (dependency) => getPlanningDependencyKey(dependency) ?? dependency
                    ),
                    calendars: dedupeRecordsById(incomingCalendars)
                }
            });
        }
    } catch (error) {
        log('Error updating IndexedDB:', error);
    }
};

export const updateEditedRecordInIdxDB = async (activeOrganizationAccount, data, storeName) => {
    try {
        const store = db.table('schedulerStores');
        const keyName = 'name';
        const record = await store
            .where(keyName)
            .equals(activeOrganizationAccount + `_${storeName}`)
            .first();

        if (record?.value) {
            const { events = [], assignments = [] } = record.value;
            const updatedEvents = events
                .filter((event) => event && typeof event === 'object' && 'id' in event)
                .map((event) => (event.id === data.event.id ? data.event : event));
            const updatedAssignments = [
                ...assignments.filter(
                    (assignment) =>
                        assignment &&
                        typeof assignment === 'object' &&
                        !(assignment.eventId === data.event.id && assignment.lineItemId === undefined)
                ),
                ...data.assignments
            ];
            await store.update(record.id, {
                value: {
                    ...record.value,
                    events: updatedEvents,
                    assignments: updatedAssignments
                }
            });
        }
    } catch (error) {
        console.warn('Error updating IndexedDB:', error);
    }
};

export const deleteRecordInIdxDB = async (activeOrganizationAccount, eventId, storeName) => {
    try {
        const store = db.table('schedulerStores');
        const keyName = 'name';
        const record = await store
            .where(keyName)
            .equals(activeOrganizationAccount + `_${storeName}`)
            .first();

        if (record?.value) {
            const { events = [], assignments = [] } = record.value;
            const updatedEvents = events.filter((event) => event.id !== eventId);
            const updatedAssignments = assignments.filter((assignment) => assignment.eventId !== eventId);
            await store.update(record.id, {
                value: {
                    ...record.value,
                    events: updatedEvents,
                    assignments: updatedAssignments
                }
            });
        }
    } catch (error) {
        console.warn('Error deleting from IndexedDB:', error);
    }
};

const updateSchedulerDataInIDB = async (
    eventID,
    activeOrganizationAccount,
    IDBSchedulerStoreName,
    updateIDBAndRefetchScheduler,
    setIsSchedulerRefetchNeeded
) => {
    if (IDBSchedulerStoreName) {
        const data = await getDataFromIDB(activeOrganizationAccount, IDBSchedulerStoreName);
        if (!isEmpty(data)) {
            remove(data.events, (e) => e.id === eventID);
            remove(data.assignments, (a) => a.eventId === eventID);
            if (Array.isArray(data.dependencies)) {
                remove(data.dependencies, (dependency) => {
                    const fromEventId =
                        typeof dependency?.fromEvent === 'object' ? dependency.fromEvent?.id : dependency?.fromEvent;
                    const toEventId =
                        typeof dependency?.toEvent === 'object' ? dependency.toEvent?.id : dependency?.toEvent;
                    const fromId = fromEventId ?? dependency?.from?.id ?? dependency?.from;
                    const toId = toEventId ?? dependency?.to?.id ?? dependency?.to;
                    const isDependencyForDeletedEvent = fromId === eventID || toId === eventID;
                    return isDependencyForDeletedEvent;
                });
            }
            await updateIDBAndRefetchScheduler(activeOrganizationAccount, data, setIsSchedulerRefetchNeeded);
        }
    }
};

export const deleteActivitiesFromSchedulerData = async (
    eventID,
    activeOrganizationAccount,
    IDBSchedulerStoreName,
    setIsSchedulerRefetchNeeded = undefined
) => {
    const updateIDBAndRefetchScheduler =
        IDBSchedulerStoreName === schedulerProductionStore
            ? updateDataToIDBAndIsSchedulerProductionRefetch
            : IDBSchedulerStoreName === schedulerPreviewStore
              ? updateDataToIDBAndIsSchedulerReservationRefetch
              : undefined;
    await updateSchedulerDataInIDB(
        eventID,
        activeOrganizationAccount,
        IDBSchedulerStoreName,
        updateIDBAndRefetchScheduler,
        setIsSchedulerRefetchNeeded
    );
};

export const updateSchedulerProductionData = async (
    eventLineitems: Array<ReturnType<typeof toSchedulerActivityItemsFromProduction>>,
    activeOrganizationAccount: string,
    application: AppDefinition,
    setIsSchedulerProductionRefetchNeeded: SetIsSchedulerProductionRefetchNeeded
) => {
    const { events, assignments } = toBryntumEventsAndAssignmentsForActivity(eventLineitems, application);
    const nextEvents = (events || []).filter((event) => event?.id);
    const nextAssignments = (assignments || []).filter((assignment) => assignment?.eventId);

    const data = await getDataFromIDB(activeOrganizationAccount, schedulerProductionStore);
    if (isEmpty(data)) return;

    data.events = (data.events || []).filter((event) => event?.id);
    data.assignments = (data.assignments || []).filter((assignment) => assignment?.eventId);

    nextEvents.forEach((event) => {
        remove(data.events, (e) => e?.id === event.id);
        remove(data.assignments, (a) => a?.eventId === event.id);
    });

    data.events = [...data.events, ...nextEvents];
    data.assignments = [...data.assignments, ...nextAssignments];

    await updateDataToIDBAndIsSchedulerProductionRefetch(
        activeOrganizationAccount,
        data,
        setIsSchedulerProductionRefetchNeeded
    );

    resetHeadlessSchedulerInitialization();
};

export const updateSchedulerReservationData = async (
    eventLineitem,
    activeOrganizationAccount,
    application,
    setIsSchedulerReservationRefetchNeeded,
    isSchedulerReservationRefetchNeeded,
    type
) => {
    const getEventsAndAssignments = () => {
        return type === SR_TYPES.Activity
            ? toBryntumEventsAndAssignmentsForActivity([eventLineitem], application)
            : toBryntumEventsAndAssignments(eventLineitem);
    };
    const { events, assignments } = getEventsAndAssignments();
    getDataFromIDB(activeOrganizationAccount, schedulerPreviewStore).then((data) => {
        if (!isEmpty(data)) {
            events.forEach((evt) => {
                remove(data.events, (e: any) => e.id === evt.id);
                remove(data.assignments, (a: any) => a.eventId === evt.id);
            });

            data.events = [...data.events, ...events];
            data.assignments = [...data.assignments, ...assignments];
            updateDataToIDBAndIsSchedulerReservationRefetch(
                activeOrganizationAccount,
                data,
                setIsSchedulerReservationRefetchNeeded,
                isSchedulerReservationRefetchNeeded
            );
            resetHeadlessSchedulerInitialization();
        }
    });
};

export const updateProductionResourceData = (
    productionData,
    activeOrganizationAccount,
    setIsSchedulerProductionRefetchNeeded
) => {
    const { production } = productionDataToBryntumFormat(productionData);
    getDataFromIDB(activeOrganizationAccount, schedulerProductionStore).then((data) => {
        if (!isEmpty(data)) {
            production.forEach((prodItem) => {
                remove(data.resources, (r: any) => r.id === prodItem.id);
            });
            data.resources = [...data.resources, ...production];
            updateDataToIDBAndIsSchedulerProductionRefetch(
                activeOrganizationAccount,
                data,
                setIsSchedulerProductionRefetchNeeded
            );
        }
    });
};

export const addNewProductionInResourceAndEventsData = (
    productionData,
    activeOrganizationAccount,
    application,
    setIsSchedulerProductionRefetchNeeded,

    changeSearch
) => {
    const eventLineItems = [];
    if (productionData.plans?.[0].activities?.length) {
        const productionEventData = {
            genre: productionData.genre,
            id: productionData.identity.id,
            otherTitles: productionData.otherTitles,
            productionStatus: productionData.productionStatus,
            publicTitle: productionData.publicTitle,
            productionType: productionData.productionType
        };
        productionData.plans[0].activities.forEach((activity) => {
            const activityItem = {
                ...activity,
                id: activity.identity.id,
                production: { ...productionEventData }
            };
            delete activityItem.identity;
            eventLineItems.push(toSchedulerActivityItemsFromProduction(activityItem));
        });
    }
    const { production } = productionDataToBryntumFormat(productionData);

    getDataFromIDB(activeOrganizationAccount, schedulerProductionStore).then((data) => {
        if (!isEmpty(data)) {
            if (eventLineItems.length) {
                const { events, assignments } = toBryntumEventsAndAssignmentsForActivity(eventLineItems, application);
                events.forEach((evt) => {
                    remove(data.events, (e: any) => e.id === evt.id);
                    remove(data.assignments, (a: any) => a.eventId === evt.id);
                });
                data.events = [...data.events, ...events];
                data.assignments = [...data.assignments, ...assignments];
            }

            production.forEach((prodItem) => {
                remove(data.resources, (r: any) => r.id === prodItem.id);
            });
            data.resources = [...data.resources, ...production];
            updateDataToIDBAndIsSchedulerProductionRefetch(
                activeOrganizationAccount,
                data,
                setIsSchedulerProductionRefetchNeeded
            );
        }
    });
    changeSearch({ newProduction: undefined });
};

export const deleteProductionResourcesFromSchedulerProductionData = async (
    productionId,
    activeOrganizationAccount,
    setIsSchedulerProductionRefetchNeeded
) => {
    getDataFromIDB(activeOrganizationAccount, schedulerProductionStore).then((data) => {
        if (!isEmpty(data)) {
            remove(data.resources, (r: any) => r.id === productionId);

            updateDataToIDBAndIsSchedulerProductionRefetch(
                activeOrganizationAccount,
                data,
                setIsSchedulerProductionRefetchNeeded
            );
        }
    });
};

export const updatePersonalSchedulerData = (
    exisitingData,
    schedulerStoreData,
    activeOrganizationAccount,
    storeName,
    setBatchSchedulerStoreData
) => {
    updateDataToIDB(activeOrganizationAccount, storeName, exisitingData);
    if (isEqual(exisitingData.resources, schedulerStoreData.resources)) {
        const confirmedEvents = [];
        const newAssignments = [];
        const newEvents = differenceBy(schedulerStoreData.events, exisitingData.events, 'id');
        newEvents.forEach((event) => {
            confirmedEvents.push(event);
            newAssignments.push({
                eventId: event.id,
                resourceId: exisitingData.resources[0].id
            });
        });

        exisitingData.events = [...exisitingData.events, ...confirmedEvents];
        exisitingData.assignments = [...exisitingData.assignments, ...newAssignments];
        setBatchSchedulerStoreData(exisitingData);
        updateDataToIDB(activeOrganizationAccount, storeName, exisitingData);
    }
};

const updateDateRangeForActivityEvent = (event, updatedEvents) => {
    const activity = updatedEvents.find((act) => act.identity.id === event.id);
    if (!activity) {
        return event;
    }
    const { mergedStart, mergedEnd } = getMergedStartEnd(activity.dateRange);
    const startValid = isValid(mergedStart);
    const endValid = isValid(mergedEnd);
    return {
        ...event,
        dateRange: activity?.dateRange,
        startDate: startValid ? toDateTimeISOWithoutOffset(mergedStart) : '',
        endDate: endValid ? toDateTimeISOWithoutOffset(mergedEnd) : '',
        duration: activity.dateRange.dateTimeDuration?.value,
        durationUnit: activity.dateRange.dateTimeDuration?.unit
    };
};

export const updateDateRangeForEventInIDB = async (storeName, activeOrganizationAccount, updatedEvents) => {
    try {
        const store = db.table('schedulerStores');
        const keyName = 'name';
        const record = await store
            .where(keyName)
            .equals(activeOrganizationAccount + `_${storeName}`)
            .first();
        const activityIds = updatedEvents.map((event) => event.identity.id);

        if (record?.value) {
            const { events = [] } = record.value;
            const updatedDBEvents = events
                .filter((event) => event && typeof event === 'object' && 'id' in event)
                .map((event) =>
                    activityIds.includes(event.id) ? updateDateRangeForActivityEvent(event, updatedEvents) : event
                );
            await store.update(record.id, {
                value: {
                    ...record.value,
                    events: updatedDBEvents
                }
            });
        }
    } catch (error) {
        log('Error updating IndexedDB:', error);
    }
};
